import _ from "lodash";

import { colors } from "theme/palette";
import { LOCALE_STRING_ZONE } from "utils/constants";
import { MONTHS } from "utils/constants";
import { format, FORMATS } from "utils/datetime";
import {
  downloadListOfListsAsXLSX,
  IColumnFormatsForXLSXExport,
} from "utils/xlsx";

import { IHeader } from "components/StyledTable";

import Cell from "entities/AssetManagement/components/Table/components/Cell";
import NameCell from "entities/AssetManagement/components/Table/components/NameCell";
import { CELL_DEFAULT_WIDTH } from "entities/AssetManagement/components/Table/constants";
import {
  AMColumn,
  AMPeriodOverPeriod,
  AMVarianceDisplayMode,
} from "entities/AssetManagement/constants";
import {
  IAssetManagementCategory,
  IAssetManagementDataV2,
  IAssetManagementRowOldStructure,
  IAssetManagementRows,
} from "entities/AssetManagement/sdk";

import { removeNonDigits } from "./components/Cell/utils";
import YearCell from "./components/YearCell";

export interface ITableData {
  itemKey: string;
  label: string;
  itemCode: string;
  isTotalRow: boolean;
  isCalculationItem: boolean;
  infoText: string;
  isAdded: boolean;
  isDeleted: boolean;
  values: Array<{
    id: number | null;
    itemKey: string;
    value: string;
    newValue?: string;
    tooltipTitle?: string;
  }>;
}
export const extractRowKey = ({ itemKey }: { itemKey: string }) => {
  const [itemCode, categoryId, subCategoryName] = itemKey.split("|");
  return { itemCode, categoryId, subCategoryName };
};

export const extractCellKey = ({ itemKey }: { itemKey: string }) => {
  const [
    itemCode,
    categoryId,
    subCategoryName,
    year,
    quarter,
    month,
    dataType,
  ] = itemKey.split("|");
  return {
    itemCode,
    categoryId,
    subCategoryName,
    year,
    quarter,
    month,
    dataType,
  };
};

export const generateRowItemKey = ({
  row,
}: {
  row: IAssetManagementRows["rows"][number];
}) =>
  [row.item_code, row.sub_category.category.id, row.sub_category.name]
    .filter((item) => !_.isNil(item))
    .join("|");

export const generateCellItemKey = ({
  row,
  value,
}: {
  row: IAssetManagementRows["rows"][number];
  value: IAssetManagementRows["rows"][number]["values"][number];
}) =>
  `${generateRowItemKey({ row })}|${value.date_info.year}|${
    value.date_info.quarter || ""
  }|${value.date_info.month || ""}|${value.data_type}`;

const formatValue = ({
  value,
}: {
  value: IAssetManagementRows["rows"][number]["values"][number];
}) => {
  if (value.data_type === AMColumn.VARIANCE_PERCENTAGE) {
    return `${value.value?.toFixed(2)}`;
  }

  return String(value.value?.toLocaleString(LOCALE_STRING_ZONE) || "");
};

export const apiToTableData = (
  row: IAssetManagementRows["rows"][number]
): ITableData => ({
  itemKey: generateRowItemKey({ row }),
  label: row.name,
  itemCode: row.item_code,
  isTotalRow: row.is_total_row,
  isCalculationItem: row.is_calculation_item,
  infoText: row.sub_category_info_text,
  isAdded: _.isNull(row.id),
  isDeleted: false,
  values: row.values.map((value) => ({
    id: value.id,
    itemKey: generateCellItemKey({ row, value }),
    value: formatValue({ value }),
  })),
});

export const addTooltipTitleToValues = ({
  row,
  columns,
}: {
  row: ITableData;
  columns: string[];
}) => {
  if (columns.length === 2) {
    const tooltipTitle = `((${columns[0]} - ${columns[1]}) / ${columns[1]}) * 100`;

    return {
      ...row,
      values: row.values.map((value) => ({
        ...value,
        tooltipTitle: _.isNull(value.id) ? tooltipTitle : "", // For the variance column, the id is null. We want to show the tooltip only for it.
      })),
    };
  }

  return row;
};

const addVarianceDataToValues = ({
  row,
  columns,
  varianceDisplayMode,
}: {
  row: IAssetManagementRows["rows"][number];
  columns: string[];
  varianceDisplayMode: Array<AMVarianceDisplayMode>;
}) => {
  if (columns.length <= 2) {
    const rowWithVarianceData = _.cloneDeep(row);
    const groupedValues = _.groupBy(row.values, (value) => {
      const itemCode = row.item_code || "";
      const subCategory = row.sub_category.name || "";
      const categoryId = row.sub_category.category.id;
      const year = value.date_info.year;
      const quarter = value.date_info.quarter || "";
      const month = value.date_info.month || "";

      return `${itemCode}|${categoryId}|${subCategory}|${year}|${quarter}|${month}`;
    });

    const keys = Object.keys(groupedValues);

    const keyIndexes = _.range(keys.length);

    keyIndexes.forEach((i) => {
      const groupKey = keys[i];
      const nextGroup = keys[i + 1];

      const { year, quarter, month } = extractCellKey({ itemKey: groupKey });
      const values = groupedValues[groupKey];

      let varianceAbsoluteValue = null;
      let variancePercentageValue = null;

      if (varianceDisplayMode.includes(AMVarianceDisplayMode.ABSOLUTE)) {
        if (columns.length === 1 && !_.isUndefined(nextGroup)) {
          varianceAbsoluteValue = calculateVariance({
            values: [...groupedValues[nextGroup], ...values],
            columns,
            valueType: AMVarianceDisplayMode.ABSOLUTE,
          });
        }

        if (columns.length === 2) {
          varianceAbsoluteValue = calculateVariance({
            values,
            columns,
            valueType: AMVarianceDisplayMode.ABSOLUTE,
          });
        }
      }

      if (varianceDisplayMode.includes(AMVarianceDisplayMode.PERCENTAGE)) {
        if (columns.length === 1 && !_.isUndefined(nextGroup)) {
          variancePercentageValue = calculateVariance({
            values: [...groupedValues[nextGroup], ...values],
            columns,
            valueType: AMVarianceDisplayMode.PERCENTAGE,
          });
        }

        if (columns.length === 2) {
          variancePercentageValue = calculateVariance({
            values,
            columns,
            valueType: AMVarianceDisplayMode.PERCENTAGE,
          });
        }
      }
      // If the variance is null, we don't want to add it to the row values
      if (!_.isNull(varianceAbsoluteValue)) {
        rowWithVarianceData.values.push({
          id: null,
          value: varianceAbsoluteValue,
          data_type: AMColumn.VARIANCE_ABSOLUTE,
          date_info: {
            year: Number(year),
            quarter: quarter ? Number(quarter) : null,
            month: month ? Number(month) : null,
          },
        });
      }

      if (!_.isNull(variancePercentageValue)) {
        rowWithVarianceData.values.push({
          id: null,
          value: variancePercentageValue,
          data_type: AMColumn.VARIANCE_PERCENTAGE,
          date_info: {
            year: Number(year),
            quarter: quarter ? Number(quarter) : null,
            month: month ? Number(month) : null,
          },
        });
      }
    });
    return rowWithVarianceData;
  }

  return row;
};

const findNextGroupKey = ({
  allKeys,
  keyToSearchBy,
}: {
  allKeys: Array<string>;
  keyToSearchBy: string;
}): string | undefined => {
  // First we need to sort the keys
  const sortedKeys = allKeys.sort((firstKey, secondKey) => {
    const {
      year: firstKeyYear,
      quarter: firstKeyQuarter,
      month: firstKeyMonth,
    } = extractCellKey({ itemKey: `|${firstKey}` });
    const {
      year: secondKeyYear,
      quarter: secondKeyQuarter,
      month: secondKeyMonth,
    } = extractCellKey({ itemKey: `|${secondKey}` });

    if (firstKeyYear !== secondKeyYear) {
      return Number(secondKeyYear) - Number(firstKeyYear);
    }

    if (firstKeyQuarter !== "" && secondKeyQuarter !== "") {
      return Number(secondKeyQuarter) - Number(firstKeyQuarter);
    }

    if (firstKeyMonth !== "" && secondKeyMonth !== "") {
      return Number(secondKeyMonth) - Number(firstKeyMonth);
    }

    return Infinity;
  });

  // Then we need to find the index of the keyToSearchBy
  const indexOfKeyToSearchBy = sortedKeys.findIndex(
    (key) => key === keyToSearchBy
  );

  // Finally we need to return the next key
  if (indexOfKeyToSearchBy + 1 <= allKeys.length) {
    return sortedKeys[indexOfKeyToSearchBy + 1];
  }
};

export const calculateTotalVariance = ({
  totalValues,
  columns,
}: {
  totalValues: { [key: string]: number };
  columns: Array<string>;
}) => {
  const totalValueKeys = Object.keys(totalValues);
  const totalValueKeysWhichMatchSelectedColumns = totalValueKeys.filter(
    (key) => {
      const { dataType } = extractCellKey({
        itemKey: `|${key}`,
      });

      return columns.includes(dataType);
    }
  );

  totalValueKeys.forEach((key) => {
    const { categoryId, subCategoryName, year, quarter, month, dataType } =
      extractCellKey({
        itemKey: `|${key}`, // total key does not have itemCode
      });

    if (
      dataType === AMColumn.VARIANCE_ABSOLUTE ||
      dataType === AMColumn.VARIANCE_PERCENTAGE
    ) {
      let firstNumber, secondNumber;

      let firstKey = `${categoryId}|${subCategoryName}|${year}|${quarter}|${month}|${columns[0]}`;
      let secondKey = `${categoryId}|${subCategoryName}|${year}|${quarter}|${month}|${columns[1]}`;

      if (columns.length === 1) {
        let nextGroupKey = findNextGroupKey({
          allKeys: totalValueKeysWhichMatchSelectedColumns,
          keyToSearchBy: firstKey,
        });

        if (_.isNil(nextGroupKey)) {
          return null;
        }

        secondKey = nextGroupKey;
      }

      firstNumber = _.get(totalValues, firstKey);
      secondNumber = _.get(totalValues, secondKey);

      if (_.isNil(firstNumber) || _.isNil(secondNumber) || secondNumber === 0) {
        return null;
      }

      const difference = firstNumber - secondNumber;

      if (dataType === AMColumn.VARIANCE_ABSOLUTE) {
        return (totalValues[key] = difference);
      }

      return (totalValues[key] = (difference / secondNumber) * 100);
    }

    return totalValues[key];
  });

  return totalValues;
};

const calculateVariance = ({
  values,
  columns,
  valueType,
}: {
  values: IAssetManagementRows["rows"][number]["values"];
  columns: string[];
  valueType: AMVarianceDisplayMode;
}) => {
  const firstNumberIndex = _.findIndex(
    values,
    (value) => value.data_type === columns[0]
  );
  let firstNumber: number | null | undefined = values[firstNumberIndex]?.value;

  const numbersAreFromTheSameType = columns.length === 1;

  let secondNumber = _.find(
    values,
    (value) =>
      value.data_type === (numbersAreFromTheSameType ? columns[0] : columns[1]),
    numbersAreFromTheSameType ? firstNumberIndex + 1 : undefined
  )?.value;

  if (numbersAreFromTheSameType) {
    // If the numbers are from the same type, we need to swap them as the variance is actually a Period over Period calculation.
    [firstNumber, secondNumber] = [secondNumber, firstNumber];
  }

  if (
    _.isNil(firstNumber) ||
    _.isNil(secondNumber) ||
    (secondNumber === 0 && valueType === AMVarianceDisplayMode.PERCENTAGE) ||
    secondNumber === Infinity ||
    firstNumber === Infinity
  ) {
    return Infinity;
  }

  const difference = firstNumber - secondNumber;

  if (valueType === AMVarianceDisplayMode.ABSOLUTE) {
    return difference;
  }

  return (difference / secondNumber) * 100;
};

export const nameColumn = ({
  selectedTab,
  setCollapsedSubCategories,
  collapsedSubCategories,
  editMode,
  handleRowDelete,
  deletedRowKeys,
}: any): IHeader<{
  label: string;
  itemCode: string;
  isTotalRow: boolean;
  infoText: string;
  isCalculationItem: boolean;
  isAdded: boolean;
}> => ({
  itemKey: "-",
  label: selectedTab.name,
  width: "350px",
  sticky: true,
  renderHeader: ({ label, Wrapper }) => (
    <Wrapper
      style={{
        height: "49px",
        fontSize: "16px",
        display: "flex",
        alignItems: "center",
        borderBottom: `1px solid ${colors.gray100}`,
      }}
    >
      {label}
    </Wrapper>
  ),
  style: {
    zIndex: 2,
  },
  children: [
    {
      itemKey: "name",
      label: "Item code & name",
      width: "350px",
      render: ({ itemKey, row, index, Wrapper }) => (
        <NameCell
          itemKey={itemKey}
          row={row}
          index={index}
          Wrapper={Wrapper}
          setCollapsedSubCategories={setCollapsedSubCategories}
          collapsedSubCategories={collapsedSubCategories}
          editMode={editMode}
          handleRowDelete={handleRowDelete}
          isDeleted={deletedRowKeys.includes(row.itemKey)}
        />
      ),
      renderHeader: ({ itemKey, label, Wrapper }) => <Wrapper>{label}</Wrapper>,
    },
  ],
});

export const transformApiHeaderToTableHeader = ({
  header,
  editMode,
  editHeaders,
  columns,
  onChange,
  allSelectedHeaders,
  yearMappings,
  updateYearMapping,
  varianceDisplayMode,
}: {
  header: IAssetManagementDataV2["headers"][number];
  editMode: boolean;
  editHeaders: boolean;
  columns: string[];
  onChange: (args: {
    itemKey: string;
    value: number | null;
    prevValue: string | null;
  }) => void;
  allSelectedHeaders: IAssetManagementDataV2["headers"];
  yearMappings: { [key: string]: string };
  updateYearMapping: (args: {
    oldYear: string | number;
    newYear: string;
  }) => void;
  varianceDisplayMode: Array<AMVarianceDisplayMode>;
}): IHeader<{
  isTotalRow: boolean;
  isCalculationItem: boolean;
  isDeleted: boolean;
  isAdded: boolean;
}> => {
  const currentSelectedYears = allSelectedHeaders.map(({ year }) =>
    _.get(yearMappings, year, year)
  );

  const isQuarterly = !_.isNil(header.quarter);
  const isMonthly = !_.isNil(header.month);
  const isYearly = !isQuarterly && !isMonthly;

  const headersForTheSameTimePeriod = allSelectedHeaders.filter((header) => {
    if (isQuarterly) {
      return !_.isNil(header.quarter);
    }

    if (isMonthly) {
      return !_.isNil(header.month);
    }

    return _.isNil(header.quarter) && _.isNil(header.month);
  });

  let varianceColumnName = "Variance";

  if (columns.length === 1) {
    if (isQuarterly) {
      varianceColumnName = "QoQ";
    }
    if (isMonthly) {
      varianceColumnName = "MoM";
    }
    if (isYearly) {
      varianceColumnName = "YoY";
    }
  }

  // Will be used to determine whether a custom column needs to be added later on.
  const currentHeaderIsTheLastOne =
    headersForTheSameTimePeriod[headersForTheSameTimePeriod.length - 1] ===
    header;

  const headers: IHeader<{
    isTotalRow: boolean;
    isCalculationItem: boolean;
    isDeleted: boolean;
    isAdded: boolean;
  }> = {
    itemKey: `${_.get(yearMappings, header.year, header.year)}|${
      header.quarter || ""
    }|${header.month || ""}`,
    label: `${_.get(yearMappings, header.year, header.year)}${
      header.quarter ? " Q" + header.quarter : ""
    }${header.month ? ` ${MONTHS[header.month - 1]}` : ""}`,
    width: columns.length * CELL_DEFAULT_WIDTH + "px",
    renderHeader: ({ itemKey, label, Wrapper }) => (
      <YearCell
        label={label}
        itemKey={itemKey}
        Wrapper={(props) => (
          <Wrapper
            {...props}
            style={{
              ...props.style,
              height: "49px",
              display: "flex",
              alignItems: "center",
            }}
          />
        )}
        editMode={editHeaders}
        currentSelectedYears={currentSelectedYears}
        onChange={({ newYear }: { newYear: string }) =>
          updateYearMapping({ oldYear: header.year, newYear })
        }
      />
    ),
    children:
      columns.map((dataType) => ({
        itemKey: `${_.get(yearMappings, header.year, header.year)}|${
          header.quarter || ""
        }|${header.month || ""}|${dataType}`,
        label: dataType,
        renderHeader: ({ label, Wrapper }) => (
          <Wrapper style={{ textAlign: "right" }}>{label}</Wrapper>
        ),
        render: ({ itemKey, row, index, Wrapper }) => (
          <Cell
            itemKey={itemKey}
            row={row}
            index={index}
            Wrapper={Wrapper}
            editMode={editMode}
            onChange={onChange}
          />
        ),
      })) || [],
  };

  if (
    !editMode &&
    (columns.length === 2 ||
      (columns.length === 1 && !currentHeaderIsTheLastOne)) &&
    !_.isUndefined(headers.children)
  ) {
    let totalColumnCount = columns.length;

    if (varianceDisplayMode.includes(AMVarianceDisplayMode.ABSOLUTE)) {
      totalColumnCount += 1;

      headers.children.push({
        itemKey: `${_.get(yearMappings, header.year, header.year)}|${
          header.quarter || ""
        }|${header.month || ""}|${AMColumn.VARIANCE_ABSOLUTE}`,
        label: `${varianceColumnName} Value`,
        renderHeader: ({ label, Wrapper }) => (
          <Wrapper style={{ textAlign: "right" }}>{label}</Wrapper>
        ),
        render: ({ itemKey, row, index, Wrapper }) => (
          <Cell
            itemKey={itemKey}
            row={row}
            index={index}
            Wrapper={Wrapper}
            editMode={false}
            onChange={() => {}}
          />
        ),
      });
    }

    if (varianceDisplayMode.includes(AMVarianceDisplayMode.PERCENTAGE)) {
      totalColumnCount += 1;

      headers.children.push({
        itemKey: `${_.get(yearMappings, header.year, header.year)}|${
          header.quarter || ""
        }|${header.month || ""}|${AMColumn.VARIANCE_PERCENTAGE}`,
        label: `${varianceColumnName} %`,
        renderHeader: ({ label, Wrapper }) => (
          <Wrapper style={{ textAlign: "right" }}>{label}</Wrapper>
        ),
        render: ({ itemKey, row, index, Wrapper }) => (
          <Cell
            itemKey={itemKey}
            row={row}
            index={index}
            Wrapper={Wrapper}
            editMode={false}
            onChange={() => {}}
          />
        ),
      });
    }

    headers.width = totalColumnCount * CELL_DEFAULT_WIDTH + "px";
  }

  return headers;
};

const calculateTotals = ({
  result,
  collapsedSubCategories,
  editMode,
}: {
  result: ITableData[];
  collapsedSubCategories: Array<string>;
  editMode: boolean;
}) => {
  let resultWithTotals: ITableData[] = [];
  const subCategoriesGrouped = _.groupBy(result, (row) => {
    if (!editMode) {
      if (row.isTotalRow) {
        return row.itemKey;
      }
    }
    const { categoryId, subCategoryName } = extractRowKey({
      itemKey: row.itemKey,
    });
    return `${categoryId}|${subCategoryName}`;
  });

  Object.keys(subCategoriesGrouped).forEach((subCategoryInfo) => {
    const subCategories = subCategoriesGrouped[subCategoryInfo];

    // In edit mode we calculate the total rows here
    // This way we can recalculate them, when changing a value in a normal data rows
    if (editMode) {
      const totalValues: { [key: string]: number } = {};

      subCategories
        .filter((row) => !row.isTotalRow && !row.isCalculationItem)
        .forEach((row) => {
          row.values.forEach(
            (value: {
              id: number | null;
              itemKey: string;
              value: string;
              newValue?: string;
            }) => {
              const {
                categoryId,
                subCategoryName,
                year,
                quarter,
                month,
                dataType,
              } = extractCellKey({
                itemKey: value.itemKey,
              });
              const key = `${categoryId}|${subCategoryName}|${year}|${quarter}|${month}|${dataType}`;

              // newValue and value are strings
              const cellValue =
                removeNonDigits(_.get(value, "newValue", value.value)) || 0;
              const valueTotal = Number(cellValue);
              totalValues[key] = _.get(totalValues, key, 0) + valueTotal;
            }
          );
        });

      const subCategoryName = subCategoryInfo.split("|")[1];

      const total = {
        itemKey: subCategoryInfo,
        label: subCategoryName === "null" ? "-" : subCategoryName, // the subcategory 'Debt Service' don't have a category
        itemCode: "",
        isTotalRow: true,
        isDeleted: false,
        isAdded: false,
        isCalculationItem: false,
        infoText: "",
        values: Object.keys(totalValues).map((key) => ({
          id: null,
          itemKey: key,
          value: totalValues[key].toLocaleString(LOCALE_STRING_ZONE),
        })),
      };

      if (collapsedSubCategories.indexOf(subCategoryName) === -1) {
        resultWithTotals = [...resultWithTotals, total, ...subCategories];
      } else {
        resultWithTotals = [...resultWithTotals, total];
      }
    } else {
      const totalRow = subCategories.find((row) => row.isTotalRow);
      const detailRows = subCategories.filter((row) => !row.isTotalRow);

      const subCategoryName = subCategoryInfo.split("|")[1];
      if (totalRow) {
        if (collapsedSubCategories.indexOf(subCategoryName) === -1) {
          resultWithTotals = [...resultWithTotals, totalRow, ...detailRows];
        } else {
          resultWithTotals = [...resultWithTotals, totalRow];
        }
      } else {
        resultWithTotals = [...resultWithTotals, ...detailRows];
      }
    }
  });

  return resultWithTotals;
};

export const transformTableData = ({
  rows,
  selectedTab,
  collapsedSubCategories,
  changedCells,
  deletedRowKeys,
  columns,
  yearMappings,
  varianceDisplayMode,
  editMode = false,
}: {
  rows: IAssetManagementRows["rows"];
  collapsedSubCategories: Array<string>;
  changedCells: {
    [key: string]: {
      current_value: number | null;
      previous_value: number | null;
    };
  };
  deletedRowKeys: Array<string>;
  columns: Array<string>;
  yearMappings: { [key: string]: string };
  varianceDisplayMode: Array<AMVarianceDisplayMode>;
  selectedTab: IAssetManagementCategory;
  editMode?: boolean;
}) => {
  let result: ITableData[] = [];

  const groupedRows = _.groupBy(rows, "sub_category.category.id");

  Object.keys(groupedRows).forEach((categoryId) => {
    if (Number(categoryId) === selectedTab.id) {
      const rows = groupedRows[categoryId];

      rows.forEach((row) => {
        const rowWithVarianceData = addVarianceDataToValues({
          row,
          columns,
          varianceDisplayMode,
        });

        const rowWithVarianceDataAndMappedYear = {
          ...rowWithVarianceData,
          values: _.map(rowWithVarianceData.values, (value) => ({
            ...value,
            date_info: {
              ...value.date_info,
              year: _.toNumber(
                _.get(
                  yearMappings,
                  value.date_info.year.toString(),
                  value.date_info.year
                )
              ),
            },
          })),
        };

        const currentRowTableData = apiToTableData(
          rowWithVarianceDataAndMappedYear
        );

        const currentRowItem = addTooltipTitleToValues({
          row: currentRowTableData,
          columns,
        });

        result.push(currentRowItem);
      });
    }
  });

  const updatedCellItemKeys: Array<string> = [];

  // Update values that already exist
  result = result.map((row) => ({
    ...row,
    isDeleted: deletedRowKeys.includes(row.itemKey),
    values: row.values.map((value: any) => {
      const changedCell = changedCells[value.itemKey];

      if (!_.isUndefined(changedCell)) {
        updatedCellItemKeys.push(value.itemKey);

        return {
          ...value,
          newValue:
            changedCell?.current_value?.toLocaleString(LOCALE_STRING_ZONE),
        };
      }

      return value;
    }),
  }));

  const newCells = _.omit(changedCells, updatedCellItemKeys);

  result = result.map((row) => {
    const keysOfCellsForRow = Object.keys(newCells).filter((key) =>
      key.includes(row.itemKey)
    );
    const cellsForRow: {
      [key: string]: any;
    } = _.pick(newCells, keysOfCellsForRow);

    const newValues = Object.keys(cellsForRow).map((key) => ({
      id: null,
      itemKey: key,
      value: "",
      newValue:
        cellsForRow[key].current_value.toLocaleString(LOCALE_STRING_ZONE),
    }));

    return {
      ...row,
      values: [...row.values, ...newValues],
    };
  });

  // calculate totals for values
  return calculateTotals({ result, collapsedSubCategories, editMode });
};

// value is event.target.value
export const getUpdatedCellValue = ({ value }: { value: any }) => {
  let newValue = value;

  // This will remove any non-numbers like "000"
  if (newValue !== "") {
    newValue = parseFloat(newValue);
  }

  return !_.isNaN(newValue) ? newValue.toString() : "";
};

const _formatValueForExtraction = ({
  value,
  itemKey,
}: {
  value: string;
  itemKey: string;
}) => {
  let raw = value;

  if (typeof raw === "string") {
    raw = raw.trim();

    raw = raw.split(",").join("");
  }

  if (raw.toLowerCase() === "infinity") {
    return "∞";
  }

  let num = Number(raw);
  if (isNaN(num) || raw === "") return "N/A";
  else if (itemKey.endsWith(AMColumn.VARIANCE_PERCENTAGE)) {
    return num / 100;
  }
  return num;
};

export const exportTableAsXLSX = ({
  headers,
  rows,
  dealName,
}: {
  headers: Array<{
    label: string;
    children: Array<{ label: string; itemKey: string }>;
    itemKey: string;
  }>;
  rows: Record<string, Array<ITableData>>;
  dealName?: string;
}) => {
  const columns = ["Category", "Sub-Category", "Item Name", "Item Code"];

  const headersData = [
    ...columns,
    ...headers
      .filter((header) => header.itemKey !== "-")
      .flatMap((header) =>
        header.children.map((subHeader) => {
          let label = subHeader.label;

          if (
            !Object.values(AMPeriodOverPeriod).some(
              (period) => label === period
            )
          ) {
            if (subHeader.itemKey.endsWith(AMColumn.VARIANCE_ABSOLUTE)) {
              label = AMColumn.VARIANCE_ABSOLUTE;
            } else if (
              subHeader.itemKey.endsWith(AMColumn.VARIANCE_PERCENTAGE)
            ) {
              label = AMColumn.VARIANCE_PERCENTAGE;
            }
          }

          return `${header.label} / ${label}`;
        })
      ),
  ];

  const orderedItemKeys = headers
    .filter((header) => header.itemKey !== "-")
    .flatMap((header) => header.children)
    .map((child) => child.itemKey);

  const rowsData: Array<Array<string | number>> = [];

  Object.entries(rows).forEach(([tabName, rowList]) => {
    let currentSubCategory = "";

    rowList.forEach((row) => {
      const sortedValues = orderedItemKeys.map((key) => {
        const match = row.values.find((v) => v.itemKey.endsWith(key));
        return !_.isNil(match)
          ? match
          : {
              id: null,
              itemKey: key,
              value: "N/A",
            };
      });

      const values = sortedValues.map((v) =>
        _formatValueForExtraction({ value: v.value, itemKey: v.itemKey })
      );

      if (row.isTotalRow) {
        currentSubCategory = row.label;

        rowsData.push([
          tabName,
          currentSubCategory === ""
            ? "(Missing Sub-Category)"
            : currentSubCategory,
          currentSubCategory === ""
            ? "(Missing Sub-Category) - Total"
            : `${currentSubCategory} - Total`,
          row.itemCode === "" ? "N/A" : row.itemCode,
          ...values,
        ]);
      } else if (row.isCalculationItem) {
        currentSubCategory = row.label;

        rowsData.push([
          tabName,
          currentSubCategory,
          currentSubCategory,
          row.itemCode === "" ? "N/A" : row.itemCode,
          ...values,
        ]);
      } else {
        if (row.itemKey === "Total") {
          row.itemCode = "TOTALS";
          currentSubCategory = "TOTALS";
          row.label = `${tabName} + Total`;
        }
        rowsData.push([
          tabName,
          currentSubCategory === ""
            ? "(Missing Sub-Category)"
            : currentSubCategory,
          row.label,
          row.itemCode,
          ...values,
        ]);
      }
    });
  });

  const columnFormats: IColumnFormatsForXLSXExport = {
    dateColumns: [],
    dateTimeColumns: [],
    currencyColumns: [],
    integerColumns: [],
    floatColumns: [],
    percentageColumns: [],
  };

  headersData.forEach((col, colIndex) => {
    if (
      col.endsWith(AMColumn.ACTUAL) ||
      col.endsWith(AMColumn.AUDITED) ||
      col.endsWith(AMColumn.BUDGETED) ||
      col.endsWith(AMColumn.VARIANCE_ABSOLUTE) ||
      col.endsWith(AMPeriodOverPeriod.MOM_VALUE) ||
      col.endsWith(AMPeriodOverPeriod.QOQ_VALUE) ||
      col.endsWith(AMPeriodOverPeriod.YOY_VALUE)
    ) {
      columnFormats.currencyColumns.push(colIndex);
    } else if (
      col.endsWith(AMColumn.VARIANCE_PERCENTAGE) ||
      col.endsWith(AMPeriodOverPeriod.MOM_PERCENTAGE) ||
      col.endsWith(AMPeriodOverPeriod.QOQ_PERCENTAGE) ||
      col.endsWith(AMPeriodOverPeriod.YOY_PERCENTAGE)
    ) {
      columnFormats.percentageColumns.push(colIndex);
    }
  });

  const preparedData = [headersData, ...rowsData];
  const formattedDatetime = format(new Date(), FORMATS.FRONTEND.DATETIME);

  downloadListOfListsAsXLSX(
    preparedData,
    `${dealName}_AM_Data_${formattedDatetime}.xlsx`,
    columnFormats
  );
};

interface IAppendSummedDataValuesArgs {
  rows: Array<ITableData>;
  dataTypes: Array<string>;
  currentYear: number;
  sourceDataRows: Array<IAssetManagementRowOldStructure>;
}

export const insertYTDRows = ({
  rows,
  dataTypes,
  currentYear,
  sourceDataRows,
}: IAppendSummedDataValuesArgs) => {
  if (dataTypes.length === 0) {
    return rows;
  }

  return rows.map((row) => {
    const newValues = dataTypes.map((dataType) => {
      const filteredValues = sourceDataRows
        .filter((sourceRow) => !sourceRow.is_total_row)
        .filter((sourceRow) => {
          return _.isNil(row.itemCode) || _.isEmpty(row.itemCode)
            ? `${sourceRow.sub_category.category.id}|${sourceRow.sub_category.name}` ===
                row.itemKey ||
                // Additional check in cases, when the subCategory is an empty string
                (_.isNil(sourceRow.sub_category) && row.itemKey === "")
            : sourceRow.item_code === row.itemCode;
        })
        .flatMap((r) => r.values)
        .filter(
          (value) =>
            value.data_type === dataType &&
            value.date_info.year === currentYear &&
            value.date_info.month !== null &&
            value.date_info.quarter === null
        )
        .map((value) => value.value);

      const summedValue = _.sum(filteredValues);

      return {
        id: null,
        itemKey: `${row.itemKey}|${currentYear}-YTD|||${dataType}`,
        value: summedValue.toLocaleString(LOCALE_STRING_ZONE),
      };
    });

    return {
      ...row,
      values: [...row.values, ...newValues],
    };
  });
};
export const appendTotalRow = ({
  rows,
  categoryName,
}: {
  rows: ITableData[];
  categoryName: string;
}) => {
  const allTotalValueKeys = rows
    .filter((row) => row.isTotalRow)
    .map((row) => row.values.map((value) => value.itemKey))
    .flat();

  const allUniqueTotalValueKeysWithoutItemCode = _.uniq(
    allTotalValueKeys.map((key) => key.split("|").slice(2).join("|"))
  );

  const totalValues = allUniqueTotalValueKeysWithoutItemCode.map((key) => ({
    id: null,
    itemKey: `Total|${key}`,
    value: rows
      .filter((row) => row.isTotalRow)
      .map(
        (row) => row.values.find((value) => value.itemKey.endsWith(key))?.value
      )
      .reduce((acc, value) => {
        if (_.isNil(value)) {
          return acc;
        }

        if (_.isFinite(acc)) {
          return acc + Number(value.replace(",", "").replace(",", ""));
        } else {
          return Infinity;
        }
      }, 0)
      .toLocaleString(LOCALE_STRING_ZONE),
  }));

  if (_.isEmpty(totalValues)) {
    return rows;
  }

  const totalRow = {
    itemKey: "Total",
    label: `Total ${categoryName}`,
    itemCode: "",
    infoText: "",
    isBold: true,
    isTotalRow: false,
    isCalculationItem: false,
    isAdded: false,
    isDeleted: false,
    values: totalValues,
  };

  return [...rows, totalRow];
};
